import React from 'react';
import { useTheme } from '@emotion/react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useHistory } from 'react-router';

import { Table } from '@/components/atoms/Table';
import { Title } from '@/components/atoms/Title';
import { Text } from '@/components/atoms/Text';
import { SvyButton } from '@/components/savory/SvyButton';
import { HeaderLeft, HeaderRight, Subtitle } from '@/components/atoms/Section/Section.style';
import RouterNavigationTabs from '@/components/organisms/RouterNavigationTabs';
import DeleteModal from '@/components/atoms/DeleteModal';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { Routes } from '@/core/router/routes.types';
import { ConfirmReloadModal } from '@/pages/PromotionalBanners/components/ConfirmReloadModal/ConfirmReloadModal';
import { useRestaurantTabRoutes } from '../hooks/useRestaurantTabRoutes';

import { usePromotionalBannersVM } from './PromotionalBanners.viewmodel';
import {
    Container,
    HeaderContainer,
    Header,
    AlertContainer,
    AlertTitleRow,
    TableContainer,
} from './PromotionalBanners.style';
import { PromotionalBannerModal } from './components/PromotionalBannerModal/PromotionalBannerModal';
import { DuplicateFallbackModal } from './components/DuplicateFallbackModal/DuplicateFallbackModal';

const PromotionalBanners: React.FunctionComponent = () => {
    const {
        headers,
        rows,
        isLoading,
        hasKioskChannel,
        hasActiveKioskMenu,
        action,
        brandId,
        restaurantId,
        schedules,
        clearAction,
        openCreateModal,
        customRowProps,
        handleDelete,
        handleRowClick,
        confirmReloadCallback,
        handleConfirmReload,
        handleCancelReload,
        fallbackState,
        handleDismissFallback,
    } = usePromotionalBannersVM();

    const theme = useTheme();
    const history = useHistory();

    const tabRoutes = useRestaurantTabRoutes();

    const navigateToMenus = React.useCallback(() => {
        history.push(generatePath(Routes.RestaurantMenus, { brandId, restaurantId }));
    }, [history, brandId, restaurantId]);

    const getModalMode = (): 'create' | 'edit' | 'view' => {
        if (action.type === 'edit') return 'edit';
        if (action.type === 'view') return 'view';
        return 'create';
    };
    const modalMode = getModalMode();

    return (
        <RouterNavigationTabs routes={tabRoutes}>
            <PromotionalBannerModal
                isOpen={action.type === 'create' || action.type === 'edit' || action.type === 'view'}
                mode={modalMode}
                banner={action.banner}
                restaurantId={restaurantId}
                schedules={schedules}
                onHide={clearAction}
            />

            <DeleteModal
                isOpen={action.type === 'delete'}
                onHide={clearAction}
                onSubmit={() => action.banner && handleDelete(action.banner)}
                title="promotionalBanner.delete.title"
                description="promotionalBanner.delete.description"
            />

            <ConfirmReloadModal
                isOpen={confirmReloadCallback !== null}
                onConfirm={handleConfirmReload}
                onCancel={handleCancelReload}
            />

            {fallbackState.isOpen && fallbackState.droppedLinkType !== 'none' && (
                <DuplicateFallbackModal
                    isOpen
                    sourceName={fallbackState.sourceName}
                    droppedLinkType={fallbackState.droppedLinkType}
                    onDismiss={handleDismissFallback}
                />
            )}

            <Container>
                <HeaderContainer>
                    <Header>
                        <HeaderLeft>
                            <Title text="promotionalBanner.page.title" />
                            <Subtitle>
                                <Text text="promotionalBanner.page.subtitle" />
                            </Subtitle>
                        </HeaderLeft>
                        {hasActiveKioskMenu && (
                            <HeaderRight>
                                <SvyButton onClick={openCreateModal} size="s">
                                    <FormattedMessage id="promotionalBanner.action.create" />
                                </SvyButton>
                            </HeaderRight>
                        )}
                    </Header>
                </HeaderContainer>
                {!hasActiveKioskMenu ? (
                    <AlertContainer>
                        <AlertTitleRow>
                            <SVGIcon icon={SVGIcons.DANGER} size={18} color={theme.color.io.warning500} />
                            <Title text="promotionalBanner.page.noActiveMenu.title" />
                        </AlertTitleRow>
                        <Text text="promotionalBanner.page.noActiveMenu.desc" />
                        <SvyButton size="s" onClick={navigateToMenus} disabled={!hasKioskChannel}>
                            <FormattedMessage id="promotionalBanner.page.noActiveMenu.cta" />
                        </SvyButton>
                    </AlertContainer>
                ) : (
                    <TableContainer>
                        <Table
                            rows={rows}
                            columns={headers}
                            isLoading={isLoading}
                            isSortable
                            defaultSortBy={[{ id: 'status', desc: false }]}
                            hasPagination
                            onRowClick={handleRowClick}
                            customRowProps={customRowProps}
                            emptyMessageId="promotionalBanner.page.emptyState"
                            emptyTableChildren={
                                <SvyButton onClick={openCreateModal} size="s" style={{ marginTop: 16 }}>
                                    <FormattedMessage id="promotionalBanner.action.create" />
                                </SvyButton>
                            }
                        />
                    </TableContainer>
                )}
            </Container>
        </RouterNavigationTabs>
    );
};

export default PromotionalBanners;
